/**
 * AdminPage
 *
 * Secure admin panel for managing system prompts, A/B tests, and QA flags.
 *
 * SECURITY LAYERS:
 * 1. Client-side: Check is_admin before rendering
 * 2. Server-side: @admin_required decorator on all endpoints
 * 3. No direct navigation link (hidden route)
 * 4. Double confirmation for destructive actions
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks';
import * as adminApi from '../api/admin';
import type { SystemPrompt, ABTest, ABTestResponse, QAStats, QAFlag } from '../types';

import './AdminPage.css';

type Tab = 'prompts' | 'ab-tests' | 'qa';

export function AdminPage() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<Tab>('prompts');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Prompts state
  const [prompts, setPrompts] = useState<SystemPrompt[]>([]);
  const [editingPrompt, setEditingPrompt] = useState<SystemPrompt | null>(null);
  const [showPromptModal, setShowPromptModal] = useState(false);

  // A/B Tests state
  const [abTests, setAbTests] = useState<ABTest[]>([]);
  const [editingTest, setEditingTest] = useState<ABTest | null>(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testResponses, setTestResponses] = useState<ABTestResponse[]>([]);
  const [selectedTestId, setSelectedTestId] = useState<number | null>(null);

  // QA state
  const [qaStats, setQaStats] = useState<QAStats | null>(null);
  const [qaFlags, setQaFlags] = useState<QAFlag[]>([]);

  // ============================================
  // SECURITY CHECK - Ultra strict
  // ============================================
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
      return;
    }

    if (!isLoading && isAuthenticated && !user?.is_admin) {
      // Not admin - immediate redirect
      navigate('/');
      return;
    }
  }, [isLoading, isAuthenticated, user, navigate]);

  // ============================================
  // Data Loading
  // ============================================
  const loadPrompts = useCallback(async () => {
    try {
      const data = await adminApi.getPrompts();
      setPrompts(data.prompts);
    } catch (err) {
      setError('Failed to load prompts');
      console.error(err);
    }
  }, []);

  const loadABTests = useCallback(async () => {
    try {
      const data = await adminApi.getABTests();
      setAbTests(data.tests);
    } catch (err) {
      setError('Failed to load A/B tests');
      console.error(err);
    }
  }, []);

  const loadQAData = useCallback(async () => {
    try {
      const [stats, flags] = await Promise.all([
        adminApi.getQAStats(),
        adminApi.getQAFlags(),
      ]);
      setQaStats(stats);
      setQaFlags(flags.flags);
    } catch (err) {
      setError('Failed to load QA data');
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) return;

    async function loadData() {
      setLoading(true);
      setError(null);

      await Promise.all([loadPrompts(), loadABTests(), loadQAData()]);

      setLoading(false);
    }

    loadData();
  }, [user?.is_admin, loadPrompts, loadABTests, loadQAData]);

  // ============================================
  // Prompt CRUD
  // ============================================
  const handleSavePrompt = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const data = {
      name: formData.get('name') as string,
      description: formData.get('description') as string,
      content: formData.get('content') as string,
      is_default: formData.get('is_default') === 'on',
    };

    try {
      if (editingPrompt) {
        await adminApi.updatePrompt(editingPrompt.id, data);
      } else {
        await adminApi.createPrompt(data);
      }
      setShowPromptModal(false);
      setEditingPrompt(null);
      await loadPrompts();
    } catch (err) {
      setError('Failed to save prompt');
      console.error(err);
    }
  };

  const handleDeletePrompt = async (id: number, name: string) => {
    // Double confirmation for safety
    if (!confirm(`Obrisati prompt "${name}"?`)) return;
    if (!confirm('Da li ste SIGURNI? Ova akcija je nepovratna.')) return;

    try {
      await adminApi.deletePrompt(id);
      await loadPrompts();
    } catch (err) {
      setError('Failed to delete prompt');
      console.error(err);
    }
  };

  // ============================================
  // A/B Test CRUD
  // ============================================
  const handleSaveTest = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const data = {
      name: formData.get('name') as string,
      description: formData.get('description') as string,
      prompt_a_id: parseInt(formData.get('prompt_a_id') as string, 10),
      prompt_b_id: parseInt(formData.get('prompt_b_id') as string, 10),
      traffic_percentage: parseInt(formData.get('traffic_percentage') as string, 10) || 50,
    };

    try {
      if (editingTest) {
        await adminApi.updateABTest(editingTest.id, data);
      } else {
        await adminApi.createABTest(data);
      }
      setShowTestModal(false);
      setEditingTest(null);
      await loadABTests();
    } catch (err) {
      setError('Failed to save A/B test');
      console.error(err);
    }
  };

  const handleToggleTest = async (test: ABTest) => {
    try {
      if (test.is_active) {
        await adminApi.deactivateABTest(test.id);
      } else {
        await adminApi.activateABTest(test.id);
      }
      await loadABTests();
    } catch (err) {
      setError('Failed to toggle A/B test');
      console.error(err);
    }
  };

  const handleViewResponses = async (testId: number) => {
    try {
      const data = await adminApi.getABTestResponses(testId);
      setTestResponses(data.responses);
      setSelectedTestId(testId);
    } catch (err) {
      setError('Failed to load responses');
      console.error(err);
    }
  };

  const handleDeleteTest = async (id: number, name: string) => {
    if (!confirm(`Obrisati A/B test "${name}"?`)) return;
    if (!confirm('Da li ste SIGURNI? Ova akcija je nepovratna.')) return;

    try {
      await adminApi.deleteABTest(id);
      await loadABTests();
    } catch (err) {
      setError('Failed to delete A/B test');
      console.error(err);
    }
  };

  // ============================================
  // QA Flag Updates
  // ============================================
  const handleUpdateFlag = async (flagId: number, status: 'new' | 'reviewed' | 'fixed') => {
    try {
      await adminApi.updateQAFlag(flagId, { status });
      await loadQAData();
    } catch (err) {
      setError('Failed to update flag');
      console.error(err);
    }
  };

  // ============================================
  // Render guards
  // ============================================
  if (isLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  // Security: Don't render anything if not admin
  if (!isAuthenticated || !user?.is_admin) {
    return null;
  }

  return (
    <div className="admin-page" data-testid="admin-page">
      <header className="admin-header">
        <h1>Admin Panel</h1>
        <div className="admin-user">
          {user.email}
        </div>
      </header>

      {error && (
        <div className="admin-error">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'prompts' ? 'active' : ''}`}
          onClick={() => setActiveTab('prompts')}
        >
          System Prompts
        </button>
        <button
          className={`admin-tab ${activeTab === 'ab-tests' ? 'active' : ''}`}
          onClick={() => setActiveTab('ab-tests')}
        >
          A/B Tests
        </button>
        <button
          className={`admin-tab ${activeTab === 'qa' ? 'active' : ''}`}
          onClick={() => setActiveTab('qa')}
        >
          QA Flags
        </button>
      </div>

      {loading ? (
        <div className="admin-loading">Loading data...</div>
      ) : (
        <div className="admin-content">
          {/* ============================================ */}
          {/* PROMPTS TAB */}
          {/* ============================================ */}
          {activeTab === 'prompts' && (
            <div className="admin-panel">
              <div className="panel-header">
                <h2>System Prompts ({prompts.length})</h2>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setEditingPrompt(null);
                    setShowPromptModal(true);
                  }}
                >
                  + New Prompt
                </button>
              </div>

              <div className="items-list">
                {prompts.map((prompt) => (
                  <div key={prompt.id} className="item-card">
                    <div className="item-header">
                      <h3>{prompt.name}</h3>
                      <div className="item-badges">
                        {prompt.is_default && (
                          <span className="badge badge-default">Default</span>
                        )}
                      </div>
                    </div>
                    {prompt.description && (
                      <p className="item-description">{prompt.description}</p>
                    )}
                    <pre className="item-content">{prompt.content.substring(0, 200)}...</pre>
                    <div className="item-actions">
                      <button
                        className="btn-secondary"
                        onClick={() => {
                          setEditingPrompt(prompt);
                          setShowPromptModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => handleDeletePrompt(prompt.id, prompt.name)}
                        disabled={prompt.is_default}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ============================================ */}
          {/* A/B TESTS TAB */}
          {/* ============================================ */}
          {activeTab === 'ab-tests' && (
            <div className="admin-panel">
              <div className="panel-header">
                <h2>A/B Tests ({abTests.length})</h2>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setEditingTest(null);
                    setShowTestModal(true);
                  }}
                >
                  + New Test
                </button>
              </div>

              <div className="items-list">
                {abTests.map((test) => (
                  <div key={test.id} className="item-card">
                    <div className="item-header">
                      <h3>{test.name}</h3>
                      <div className="item-badges">
                        {test.is_active && (
                          <span className="badge badge-active">Active</span>
                        )}
                      </div>
                    </div>
                    {test.description && (
                      <p className="item-description">{test.description}</p>
                    )}
                    <div className="test-prompts">
                      <span><strong>A:</strong> {test.prompt_a_name || `#${test.prompt_a_id}`}</span>
                      <span><strong>B:</strong> {test.prompt_b_name || `#${test.prompt_b_id}`}</span>
                      <span><strong>Traffic:</strong> {test.traffic_percentage}%</span>
                    </div>
                    <div className="item-actions">
                      <button
                        className={test.is_active ? 'btn-warning' : 'btn-success'}
                        onClick={() => handleToggleTest(test)}
                      >
                        {test.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => handleViewResponses(test.id)}
                      >
                        Responses
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => {
                          setEditingTest(test);
                          setShowTestModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => handleDeleteTest(test.id, test.name)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Responses Modal */}
              {selectedTestId && (
                <div className="modal-overlay" onClick={() => setSelectedTestId(null)}>
                  <div className="modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>Test Responses</h3>
                      <button className="close-btn" onClick={() => setSelectedTestId(null)}>
                        &times;
                      </button>
                    </div>
                    <div className="modal-content">
                      {testResponses.length === 0 ? (
                        <p>No responses yet.</p>
                      ) : (
                        <table className="responses-table">
                          <thead>
                            <tr>
                              <th>User</th>
                              <th>Query</th>
                              <th>Chose</th>
                              <th>Rating A</th>
                              <th>Rating B</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {testResponses.map((r) => (
                              <tr key={r.id}>
                                <td>{r.user_email}</td>
                                <td>{r.user_query.substring(0, 50)}...</td>
                                <td><strong>{r.chosen_response}</strong></td>
                                <td>{r.rating_a ?? '-'}</td>
                                <td>{r.rating_b ?? '-'}</td>
                                <td>{new Date(r.created_at).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============================================ */}
          {/* QA TAB */}
          {/* ============================================ */}
          {activeTab === 'qa' && (
            <div className="admin-panel">
              <div className="panel-header">
                <h2>QA Stats & Flags</h2>
              </div>

              {qaStats && (
                <div className="qa-stats">
                  <div className="stat-card">
                    <div className="stat-value">{qaStats.total_reviews}</div>
                    <div className="stat-label">Total Reviews</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{qaStats.flagged_count}</div>
                    <div className="stat-label">Flagged</div>
                  </div>
                  {Object.entries(qaStats.flags_by_severity).map(([severity, count]) => (
                    <div key={severity} className={`stat-card severity-${severity}`}>
                      <div className="stat-value">{count}</div>
                      <div className="stat-label">{severity}</div>
                    </div>
                  ))}
                </div>
              )}

              <h3>Recent Flags ({qaFlags.length})</h3>
              <div className="flags-list">
                {qaFlags.map((flag) => (
                  <div key={flag.id} className={`flag-card severity-${flag.severity}`}>
                    <div className="flag-header">
                      <span className="flag-type">{flag.flag_type}</span>
                      <span className={`flag-status status-${flag.status}`}>{flag.status}</span>
                    </div>
                    <p className="flag-details">{flag.details}</p>
                    <div className="flag-meta">
                      <span>Session: {flag.session_uuid.substring(0, 8)}...</span>
                      <span>{new Date(flag.created_at).toLocaleString()}</span>
                    </div>
                    <div className="flag-actions">
                      {flag.status === 'new' && (
                        <button
                          className="btn-secondary"
                          onClick={() => handleUpdateFlag(flag.id, 'reviewed')}
                        >
                          Mark Reviewed
                        </button>
                      )}
                      {flag.status === 'reviewed' && (
                        <button
                          className="btn-success"
                          onClick={() => handleUpdateFlag(flag.id, 'fixed')}
                        >
                          Mark Fixed
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* PROMPT MODAL */}
      {/* ============================================ */}
      {showPromptModal && (
        <div className="modal-overlay" onClick={() => setShowPromptModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingPrompt ? 'Edit Prompt' : 'New Prompt'}</h3>
              <button className="close-btn" onClick={() => setShowPromptModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleSavePrompt}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  defaultValue={editingPrompt?.name || ''}
                  required
                  placeholder="e.g., AstroLucy V1"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  name="description"
                  defaultValue={editingPrompt?.description || ''}
                  placeholder="Short description"
                />
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea
                  name="content"
                  defaultValue={editingPrompt?.content || ''}
                  required
                  rows={15}
                  placeholder="System prompt content..."
                />
              </div>
              <div className="form-group checkbox">
                <input
                  type="checkbox"
                  name="is_default"
                  id="is_default"
                  defaultChecked={editingPrompt?.is_default || false}
                />
                <label htmlFor="is_default">Set as default prompt</label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowPromptModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* A/B TEST MODAL */}
      {/* ============================================ */}
      {showTestModal && (
        <div className="modal-overlay" onClick={() => setShowTestModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTest ? 'Edit A/B Test' : 'New A/B Test'}</h3>
              <button className="close-btn" onClick={() => setShowTestModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveTest}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  defaultValue={editingTest?.name || ''}
                  required
                  placeholder="e.g., Prompt V1 vs V2"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  name="description"
                  defaultValue={editingTest?.description || ''}
                  placeholder="What are you testing?"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Prompt A</label>
                  <select name="prompt_a_id" defaultValue={editingTest?.prompt_a_id || ''} required>
                    <option value="">Select...</option>
                    {prompts.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Prompt B</label>
                  <select name="prompt_b_id" defaultValue={editingTest?.prompt_b_id || ''} required>
                    <option value="">Select...</option>
                    {prompts.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Traffic % (users seeing this test)</label>
                <input
                  type="number"
                  name="traffic_percentage"
                  defaultValue={editingTest?.traffic_percentage || 50}
                  min={1}
                  max={100}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowTestModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
